//#region IMPORTS
import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  isAnyOf,
} from "@reduxjs/toolkit";
import {
  InstoreTheme,
  InstoreThemeState,
  InstoreThemeParams,
} from "../../models/instoreTheme";
import { FieldValues } from "react-hook-form";
import { RootState } from "../../store/configureStore";
import { toast } from "react-toastify";
import getAxiosParams from "../../helpers/getAxiosParams";
import agent from "../../api/agent";
//#endregion

//#region INIT
const initialExtraState: InstoreThemeState = {
  instoreThemes_Loaded: false,
  instoreTheme_params: initParams(),
  searchedThemes: [],
  metaData: undefined,
  loading: false,
  status: "iddle",
};

// Set initial query parameters
function initParams(): InstoreThemeParams {
  return {
    page: 1,
    limit: 20,
  };
}
//#endregion

//#region CREATE ADAPTER
const instoreThemesAdapter = createEntityAdapter<InstoreTheme>();
//#endregion

//#region READ
// Fetch All
export const fetchInstoreThemesAsync = createAsyncThunk<
  InstoreTheme[],
  void,
  { state: RootState }
>("instoreThemes/fetchInstoreThemesAsync", async (_, thunkAPI) => {
  const instoreThemeParams = getAxiosParams(
    thunkAPI.getState().instoreThemes.instoreTheme_params
  );
  try {
    const { data, meta } = await agent.InstoreThemes.list(instoreThemeParams);
    thunkAPI.dispatch(setInstoreThemeMetaData(meta));
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});

// search All
export const searchInstoreThemesAsync = createAsyncThunk<
  InstoreTheme[],
  string,
  { state: RootState }
>("instoreThemes/searchInstoreThemesAsync", async (searchTerm, thunkAPI) => {
  const searchParams = new URLSearchParams();
  searchParams.append("limit", "10000");
  searchParams.append("name", searchTerm);
  try {
    const { data } = await agent.InstoreThemes.list(searchParams);
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});

// Fetch by id
export const fetchInstoreThemeAsync = createAsyncThunk<
  InstoreTheme,
  number,
  { state: RootState }
>("instoreThemes/fetchInstoreThemeAsync", async (instoreTheme_id, thunkAPI) => {
  try {
    const { data } = await agent.InstoreThemes.detail(instoreTheme_id);
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});
//#endregion

//#region CREATE
export const createInstoreThemeAsync = createAsyncThunk<
  InstoreTheme,
  { values: FieldValues | null }
>("instoreThemes/createInstoreThemeAsync", async ({ values }, thunkAPI) => {
  try {
    const { data } = await agent.InstoreThemes.create({ ...values });
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});
//#endregion

//#region UPDATE
export const updateInstoreThemeAsync = createAsyncThunk<
  InstoreTheme,
  { id: number; values: FieldValues }
>("instoreThemes/updateInstoreThemeAsync", async ({ id, values }, thunkAPI) => {
  try {
    const { data } = await agent.InstoreThemes.update(id, { ...values });
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});
//#endregion

//#region DELETE
export const deleteInstoreThemeAsync = createAsyncThunk<number, { id: number }>(
  "instoreThemes/deleteInstoreThemeAsync",
  async ({ id }, thunkAPI) => {
    try {
      await agent.InstoreThemes.delete(id);
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message);
    }
  }
);
//#endregion

//#region CREATE REDUCER SLICE
export const instoreThemeSlice = createSlice({
  name: "instoreThemes",
  initialState:
    instoreThemesAdapter.getInitialState<InstoreThemeState>(initialExtraState),
  reducers: {
    setInstoreThemeParams: (state, action) => {
      state.instoreThemes_Loaded = false;
      state.instoreTheme_params = {
        ...state.instoreTheme_params,
        ...action.payload,
        page: 1,
      };
    },
    clearSearchedInstoreThemes: (state) => {
      state.searchedThemes = [];
    },
    clearInstoreThemeParams: (state) => {
      state.instoreThemes_Loaded = false;
      state.instoreTheme_params = initParams();
    },
    clearInstoreThemeParam: (state, action) => {
      const newParmams = { ...state.instoreTheme_params };
      delete newParmams[action.payload];
      state.instoreThemes_Loaded = false;
      state.instoreTheme_params = newParmams;
    },
    setInstoreThemePageNumber: (state, action) => {
      state.instoreThemes_Loaded = false;
      state.instoreTheme_params = {
        ...state.instoreTheme_params,
        ...action.payload,
      };
    },
    setInstoreThemeMetaData: (state, action) => {
      state.metaData = action.payload;
    },
    resetInstoreThemeParams: (state) => {
      state.instoreThemes_Loaded = false;
      state.instoreTheme_params = initParams();
    },
    clearInstoreThemes: () =>
      instoreThemesAdapter.getInitialState<InstoreThemeState>(
        initialExtraState
      ),
  },
  extraReducers: (builder) => {
    // FETCH InstoreThemes
    builder.addCase(fetchInstoreThemesAsync.pending, (state) => {
      state.loading = true;
      state.status = "pendingFetchInstoreThemes";
    });
    builder.addCase(fetchInstoreThemesAsync.fulfilled, (state, { payload }) => {
      instoreThemesAdapter.setAll(state, payload);
      state.instoreThemes_Loaded = true;
      state.loading = false;
      state.status = "idle";
    });

    // FETCH InstoreTheme by id
    builder.addCase(fetchInstoreThemeAsync.pending, (state) => {
      state.loading = true;
      state.status = "pendingFetchInstoreTheme";
    });
    builder.addCase(fetchInstoreThemeAsync.fulfilled, (state, { payload }) => {
      instoreThemesAdapter.upsertOne(state, payload);
      state.loading = false;
      state.status = "idle";
    });

    // SEARCH InstoreThemes by name
    builder.addCase(searchInstoreThemesAsync.pending, (state) => {
      state.loading = true;
      state.status = "pendingSearchInstoreThemesAsync";
    });
    builder.addCase(
      searchInstoreThemesAsync.fulfilled,
      (state, { payload }) => {
        state.searchedThemes = payload;
        state.loading = false;
        state.status = "idle";
      }
    );

    // CREATE InstoreTheme
    builder.addCase(createInstoreThemeAsync.pending, (state) => {
      state.loading = true;
      state.status = "PendingCreateInstoreTheme";
    });
    builder.addCase(createInstoreThemeAsync.fulfilled, (state, { payload }) => {
      instoreThemesAdapter.addOne(state, payload);
      toast.success("Instore theme added");
      state.loading = false;
      state.status = "idle";
    });

    // UPDATE InstoreTheme
    builder.addCase(updateInstoreThemeAsync.pending, (state, { meta }) => {
      state.loading = true;
      state.status = "PendingUpdateInstoreTheme" + meta.arg.id;
    });
    builder.addCase(updateInstoreThemeAsync.fulfilled, (state, { payload }) => {
      instoreThemesAdapter.upsertOne(state, payload);
      toast.success(`Instore theme ${payload.name} updated`);
      state.loading = false;
      state.status = "idle";
    });

    // DELETE InstoreTheme
    builder.addCase(deleteInstoreThemeAsync.pending, (state) => {
      state.loading = true;
      state.status = "PendingDeleteInstoreTheme";
    });
    builder.addCase(deleteInstoreThemeAsync.fulfilled, (state, action) => {
      instoreThemesAdapter.removeOne(state, action.payload);
      toast.success("Instore theme deleted");
      state.loading = false;
      state.status = "idle";
    });

    //#region CRUD REJECTION
    builder.addMatcher(
      isAnyOf(
        fetchInstoreThemeAsync.rejected,
        fetchInstoreThemesAsync.rejected,
        createInstoreThemeAsync.rejected,
        updateInstoreThemeAsync.rejected,
        deleteInstoreThemeAsync.rejected,
        searchInstoreThemesAsync.rejected
      ),
      (state, { payload }) => {
        toast.error(`${payload}`);
        state.loading = false;
        state.status = "idle";
      }
    );
    //#endregion
  },
});
//#endregion

//#region NORMALISED STATE
export const instoreThemesSelectors = instoreThemesAdapter.getSelectors(
  (state: RootState) => state.instoreThemes
);
//#endregion

//#region EXPORT
export const {
  clearSearchedInstoreThemes,
  setInstoreThemePageNumber,
  setInstoreThemeMetaData,
  resetInstoreThemeParams,
  clearInstoreThemeParams,
  clearInstoreThemeParam,
  setInstoreThemeParams,
  clearInstoreThemes,
} = instoreThemeSlice.actions;
//#endregion
